/**
 * CEO Input Survey — content configuration.
 *
 * This is the single source of truth for the survey's question backbone,
 * the seeded 2026 initiative list (from the 1H26 Nellis Auction Roadmap),
 * the optional identity fields, and the involvement / medal vocabularies.
 *
 * Editing this file changes the survey. No schema change needed for content edits.
 */

// --- Identity (all optional) ---

export const ROLE_BANDS = [
  "Area Manager",
  "Operations Manager",
  "General Manager",
  "Senior Leader",
  "Home Office Team Member",
] as const;
export type RoleBand = (typeof ROLE_BANDS)[number];

// Real Nellis warehouse buildings + Home Office.
export const LOCATIONS = [
  "Dean Martin",
  "North Las Vegas",
  "Nellis Outlet",
  "Mesa",
  "Phoenix",
  "Katy",
  "SW Houston",
  "Delran",
  "Denver",
  "Home Office",
  "Other",
] as const;
export type SurveyLocation = (typeof LOCATIONS)[number];

// How deep the adaptive follow-up can go per opener (opener + up to N probes).
export const MAX_FOLLOWUPS = 2;

// --- Open-ended business-health backbone ---
// Fixed questions guarantee every leader covers the same ground. The adaptive
// AI follow-up fires only when an answer is thin (see api/survey/answer).

export type OpenQuestion = {
  key: string;
  text: string;
  /** Hint shown under the question. */
  hint?: string;
};

// Light, conversational openers. Depth comes from the adaptive, role-aware
// follow-ups (see MAX_FOLLOWUPS), not from heavy front-loaded questions.
export const OPEN_QUESTIONS: OpenQuestion[] = [
  {
    key: "checking_in",
    text: "How are things going in your world right now?",
    hint: "However you want to take that.",
  },
  {
    key: "going_well",
    text: "What's been working really well lately?",
    hint: "Big or small.",
  },
  {
    key: "the_grind",
    text: "What's felt like a grind, or harder than it should be?",
  },
  {
    key: "one_change",
    text: "If you could wave a wand and fix one thing, what would it be?",
  },
];

// --- Seeded 2026 initiatives (from 1H26 Roadmap) ---

export type Initiative = {
  key: string;
  label: string;
  owner: string;
  blurb: string;
};

export const INITIATIVES: Initiative[] = [
  {
    key: "multi_pick",
    label: "Multi-Pick",
    owner: "Ops / Cargo",
    blurb: "Refine and roll out the multi-appointment pick tool to all buildings to cut picking labor.",
  },
  {
    key: "prime",
    label: "PRIME",
    owner: "Ops",
    blurb: "A standardized diagnostic and execution framework to get every site to target EBITDA.",
  },
  {
    key: "prime_r",
    label: "PRIME(R) Reconnaissance",
    owner: "Sales",
    blurb: "The front-end intelligence phase of PRIME: a Site Insight Report before intervention.",
  },
  {
    key: "labor_iq_integration",
    label: "Labor IQ Integration",
    owner: "Ops",
    blurb: "Drive full adoption of Labor IQ as the backbone for labor performance and the bonus program.",
  },
  {
    key: "labor_iq_accountability",
    label: "Labor IQ Accountability System",
    owner: "Product",
    blurb: "Auto-flag underperformers weekly and trigger structured coaching via Paylocity Journals.",
  },
  {
    key: "talent_acquisition",
    label: "Talent Acquisition Strategy",
    owner: "HR",
    blurb: "Redesign frontline hiring to balance speed with quality of hire and long-term retention.",
  },
  {
    key: "stripe",
    label: "Stripe Migration & Cost Accounting",
    owner: "Finance / Eng",
    blurb: "Migrate to Stripe for checkout and Radar fraud, plus tighten cost accounting and fund flows.",
  },
  {
    key: "customer_analysis",
    label: "Customer Analysis & Value Insights",
    owner: "CCO",
    blurb: "Segment customers by value and activate tailored marketing and CX strategies.",
  },
  {
    key: "elevate_cx",
    label: "Elevate Customer Experience",
    owner: "CCO",
    blurb: "Build an intentional customer-experience system at the pickup stage.",
  },
  {
    key: "marketing_data",
    label: "Marketing Data Mapping",
    owner: "Marketing",
    blurb: "Fix UTM tracking and attribution across Meta, Google, and Klaviyo to know what truly works.",
  },
  {
    key: "customer_abuse",
    label: "Customer Abuse Analysis",
    owner: "Marketing",
    blurb: "Detect and act on customer abuse (fraud, chargebacks, swaps) using Stripe Radar plus internal insight.",
  },
  {
    key: "partnership_dev",
    label: "Partnership Development",
    owner: "Sales",
    blurb: "Strengthen strategic retailer relationships ahead of RFP and program expansion.",
  },
  {
    key: "brokerage_logistics",
    label: "Brokerage Traction & Logistics",
    owner: "Transportation",
    blurb: "Optimize Gavel's transport network (new TMS) and grow Quick Freight's brokerage footprint.",
  },
  {
    key: "66degrees",
    label: "66 Degrees AI Engagement",
    owner: "Engineering",
    blurb: "Expand the recommendation engine and add AI search and a site concierge for buyers.",
  },
  {
    key: "security_stability",
    label: "Security & Stability",
    owner: "Engineering",
    blurb: "Audit and harden the platform against DDoS and breaches after recent attacks.",
  },
];

// --- Involvement + medal vocabularies ---

export const INVOLVEMENT_OPTIONS = [
  { value: "directly_involved", label: "Directly involved", desc: "I helped drive or execute this." },
  { value: "aware", label: "Aware, not involved", desc: "I know it happened but did not work on it." },
  { value: "not_aware", label: "Not aware of it", desc: "This is new to me." },
] as const;
export type Involvement = (typeof INVOLVEMENT_OPTIONS)[number]["value"];

export const MEDAL_OPTIONS = [
  { value: "gold", label: "Gold", desc: "Top impact. A clear win." },
  { value: "silver", label: "Silver", desc: "Solid. Made a real difference." },
  { value: "bronze", label: "Bronze", desc: "Some value. Mixed or early." },
  { value: "didnt_land", label: "Didn't land", desc: "Did not deliver, or stalled." },
] as const;
export type Medal = (typeof MEDAL_OPTIONS)[number]["value"];
